import { Component, ElementRef, HostListener, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Preference, PREFERENCE_LABELS, RosterRef, peaksFor,
} from '../../../invitations/models/invitation.model';
import { FsComboOption } from '../../../common/fs-combobox/fs-combobox.component';
import { Composition, PoolRow, compositionOf } from '../../../reports/services/report-pool';
import { SessionMockService } from '../../services/session-mock.service';
import { SessionSource } from '../../models/session.model';

/** Wizard-facing shape — only what the picker needs, kept separate from the full Invitation model. */
export interface SessionInvitationRef { id: string; name: string; rosterName: string; }

/**
 * One row in the source chip-bar: either a whole organization or a single invitation. The two
 * kinds share one array on the wizard form (`sources: SessionSource[]`) — this is the label/lookup
 * layer over that union, generalizing the Reports picker's org-only chip bar.
 */
@Component({
  selector: 'app-session-step-participants',
  templateUrl: './step-session-participants.component.html',
  styleUrls: ['./step-session-participants.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionStepParticipantsComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() orgs: RosterRef[] = [];
  @Input() invitations: SessionInvitationRef[] = [];

  pool: PoolRow[] = [];
  composition!: Composition;

  search = '';
  view: 'all' | 'selected' = 'all';
  /** Peak chips currently filtering the pool. Empty = no filter. */
  peakFilters: Preference[] = [];
  /** Whether the "Add" panel (organizations + invitations lists) is open. */
  addOpen = false;

  readonly preferences: Preference[] = ['cl', 'id', 'dv', 'im'];
  readonly prefLabels = PREFERENCE_LABELS;

  constructor(private svc: SessionMockService, private el: ElementRef<HTMLElement>) {}

  ngOnInit(): void { this.reloadPool(); }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.addOpen && !this.el.nativeElement.contains(event.target as Node)) { this.addOpen = false; }
  }

  // --- sources (the chip bar) ---

  get sources(): SessionSource[] { return this.form.value.sources as SessionSource[]; }
  get selectedIds(): string[] { return this.form.value.participantIds as string[]; }

  /** Chip label + badge count for every source, in add order. */
  sourceChips(): Array<{ source: SessionSource; label: string; count: number }> {
    return this.sources.map(s => ({
      source: s,
      label: s.kind === 'org'
        ? (this.orgs.find(o => o.id === s.id)?.name ?? '(removed org)')
        : `Invite — ${this.invitations.find(i => i.id === s.id)?.name ?? '(removed invitation)'}`,
      count: s.kind === 'org'
        ? this.pool.filter(r => r.orgId === s.id).length
        : this.pool.filter(r => r.invitationId === s.id).length,
    }));
  }

  /** Orgs not already sourced — the "Add organization" list's options. */
  get addableOrgs(): FsComboOption[] {
    const used = new Set(this.sources.filter(s => s.kind === 'org').map(s => s.id));
    return this.orgs.filter(o => !used.has(o.id)).map(o => ({ id: o.id, name: o.name }));
  }

  /** Invitations not already sourced — the "Add invitation" list's options. */
  get addableInvitations(): FsComboOption[] {
    const used = new Set(this.sources.filter(s => s.kind === 'invitation').map(s => s.id));
    return this.invitations.filter(i => !used.has(i.id))
      .map(i => ({ id: i.id, name: `${i.name} (${i.rosterName})` }));
  }

  addSource(kind: 'org' | 'invitation', id: string): void {
    if (!id || this.sources.some(s => s.kind === kind && s.id === id)) { return; }
    this.form.get('sources')!.setValue([...this.sources, { kind, id } as SessionSource]);
    this.addOpen = false;
    this.reloadPool();
  }

  /**
   * Dropping a source also drops the people it UNIQUELY contributed — keeping them would leave the
   * session holding participants from a source the picker no longer shows, invisible and
   * unremovable. Diffed against the OLD full pool (not the filtered/visible rows) so filter-hidden
   * people are still deselected, and against the NEW pool (not just "rows matching this source")
   * so a person still covered by another remaining source (e.g. their org AND their invitation were
   * both added) is correctly kept.
   */
  removeSource(source: SessionSource): void {
    const oldPool = this.pool;
    const nextSources = this.sources.filter(s => !(s.kind === source.kind && s.id === source.id));
    this.form.get('sources')!.setValue(nextSources);
    this.svc.pool(nextSources).subscribe(rows => {
      const stillHere = new Set(rows.map(r => r.participant.id));
      const goneIds = oldPool.filter(r => !stillHere.has(r.participant.id)).map(r => r.participant.id);
      this.pool = rows;
      this.form.get('participantIds')!.setValue(this.selectedIds.filter(id => !goneIds.includes(id)));
      this.recompute();
    });
  }

  private reloadPool(): void {
    this.svc.pool(this.sources).subscribe(rows => {
      this.pool = rows;
      this.recompute();
    });
  }

  private recompute(): void { this.composition = compositionOf(this.pool, this.selectedIds); }

  // --- the list ---

  get rows(): PoolRow[] {
    let rows = this.pool;
    if (this.view === 'selected') { rows = rows.filter(r => this.isSelected(r.participant.id)); }
    if (this.peakFilters.length) {
      rows = rows.filter(r => {
        const pk = peaksFor(r.participant.profileTypeId);
        return this.peakFilters.some(f => pk.includes(f));
      });
    }
    const s = this.search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(r =>
        `${r.participant.firstName} ${r.participant.lastName}`.toLowerCase().includes(s) ||
        r.orgName.toLowerCase().includes(s) ||
        r.invitationName.toLowerCase().includes(s) ||
        (r.participant.profileType || '').toLowerCase().includes(s));
    }
    return rows;
  }

  /** app-fs-icon takes a `profile` object shaped like the API's — `profile_type_id`, not camelCase. */
  iconProfile(row: PoolRow): { profile_type_id: number | undefined } {
    return { profile_type_id: row.participant.profileTypeId };
  }

  isSelected(id: string): boolean { return this.selectedIds.includes(id); }

  toggle(row: PoolRow): void {
    const id = row.participant.id;
    const next = this.isSelected(id)
      ? this.selectedIds.filter(x => x !== id)
      : [...this.selectedIds, id];
    this.form.get('participantIds')!.setValue(next);
    this.recompute();
  }

  /** Add or remove everything currently visible — the filters ARE the bulk selector. */
  toggleAllVisible(): void {
    const visible = this.rows.map(r => r.participant.id);
    const allIn = visible.length > 0 && visible.every(id => this.isSelected(id));
    const next = allIn
      ? this.selectedIds.filter(id => !visible.includes(id))
      : [...this.selectedIds, ...visible.filter(id => !this.isSelected(id))];
    this.form.get('participantIds')!.setValue(next);
    this.recompute();
  }

  allVisibleSelected(): boolean {
    const visible = this.rows;
    return visible.length > 0 && visible.every(r => this.isSelected(r.participant.id));
  }

  peaks(row: PoolRow): Preference[] { return peaksFor(row.participant.profileTypeId); }

  togglePeakFilter(p: Preference): void {
    this.peakFilters = this.peakFilters.includes(p)
      ? this.peakFilters.filter(x => x !== p)
      : [...this.peakFilters, p];
  }

  /** How many people in the pool peak in this preference — the filter chip's badge. */
  poolPeakCount(p: Preference): number {
    return this.pool.filter(r => peaksFor(r.participant.profileTypeId).includes(p)).length;
  }

  clearFilters(): void { this.peakFilters = []; this.search = ''; this.view = 'all'; }

  // --- the rail ---

  /** Bar width as a share of the largest bar, so a small group still reads. 0 when all are 0
   *  (e.g. an all-Integrator selection, which peaks in nothing). */
  barWidth(p: Preference): number {
    const max = Math.max(...this.preferences.map(x => this.composition.peaks[x]));
    return max ? (this.composition.peaks[p] / max) * 100 : 0;
  }

  get missingLabels(): string { return this.composition.missing.map(p => this.prefLabels[p]).join(', '); }
}
